/**
 * Sound Effects Hook
 * Provides optional audio feedback for correct/incorrect answers
 */

import { useCallback, useEffect, useState } from 'react';

// Sound URLs (using simple Web Audio API tones)
const SOUNDS = {
  correct: { frequency: 523.25, duration: 150, type: 'sine' as OscillatorType }, // C5
  incorrect: { frequency: 200, duration: 200, type: 'square' as OscillatorType },
  click: { frequency: 1000, duration: 50, type: 'sine' as OscillatorType },
  hint: { frequency: 440, duration: 100, type: 'triangle' as OscillatorType },
  complete: [
    { frequency: 523.25, duration: 100, delay: 0 },    // C5
    { frequency: 659.25, duration: 100, delay: 100 },  // E5
    { frequency: 783.99, duration: 200, delay: 200 },  // G5
  ],
};

export const useSoundEffects = () => {
  const [enabled, setEnabled] = useState(() => {
    const stored = localStorage.getItem('soundEffects');
    return stored === 'true';
  });

  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  // Initialize AudioContext on first user interaction
  useEffect(() => {
    const initAudio = () => {
      if (!audioContext) {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        setAudioContext(ctx);
      }
    };

    // Initialize on any click
    document.addEventListener('click', initAudio, { once: true });
    return () => document.removeEventListener('click', initAudio);
  }, [audioContext]);

  // Save preference
  useEffect(() => {
    localStorage.setItem('soundEffects', String(enabled));
  }, [enabled]);

  const playTone = useCallback((
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    delay: number = 0
  ) => {
    if (!enabled || !audioContext) return;

    setTimeout(() => {
      try {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.type = type;
        oscillator.frequency.value = frequency;

        // Fade out to avoid clicks
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + duration / 1000
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration / 1000);
      } catch (e) {
        // Silently fail if audio doesn't work
      }
    }, delay);
  }, [enabled, audioContext]);

  const playCorrect = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.correct;
    playTone(frequency, duration, type);
  }, [playTone]);

  const playIncorrect = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.incorrect;
    playTone(frequency, duration, type);
  }, [playTone]);

  const playClick = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.click;
    playTone(frequency, duration, type);
  }, [playTone]);

  const playHint = useCallback(() => {
    const { frequency, duration, type } = SOUNDS.hint;
    playTone(frequency, duration, type);
  }, [playTone]);

  const playComplete = useCallback(() => {
    SOUNDS.complete.forEach(({ frequency, duration, delay }) => {
      playTone(frequency, duration, 'sine', delay);
    });
  }, [playTone]);

  const toggle = useCallback(() => {
    setEnabled(prev => !prev);
  }, []);

  return {
    enabled,
    toggle,
    playCorrect,
    playIncorrect,
    playClick,
    playHint,
    playComplete,
  };
};

export default useSoundEffects;
